import { OTPVerificationScreen } from '@/components/organisms';
import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';
import { customerDetailsService } from '@/modules/onboarding/services/customerDetailsService';
import { useRouter } from 'expo-router';
import React, { useCallback, useEffect } from 'react';
import { useOTP } from '../hooks/useOTP';
import { trackDynatraceEvent } from '@/core/services/dynatraceService';

export interface PhoneOTPScreenProps {
    phoneNumber?: string;
    progress?: number;
}

export const PhoneOTPScreen = React.memo<PhoneOTPScreenProps>(function PhoneOTPScreen({
    phoneNumber,
    progress = 0.6,
}) {
    const router = useRouter();
    const uaePassRegistration = useAppStore((state) => state.uaePassRegistration);
    const user = uaePassRegistration?.user || null;
    const derivedPhone = phoneNumber ?? user?.mobile ?? '';

    const {
        isLoading,
        error,
        isVerified,
        verifyOtp,
        resendOtp,
        clearError,
    } = useOTP({
        type: 'phone',
        contactInfo: derivedPhone,
        autoSend: true,
        sectionName: 'phoneOtp',
    });

    useEffect(() => {
        trackDynatraceEvent({
            p_screen: 'otp',
            p_actionName: 'Phone OTP Screen Viewed',
            p_sectionName: 'phoneOtp',
            eventType: 'view',
        });
    }, []);

    const handleOTPComplete = useCallback(async (otp: string) => {
        logger.info('PhoneOTPScreen', 'OTP entered, verifying...', { otp });
        const verified = await verifyOtp(otp);

        if (verified) {
            logger.info('PhoneOTPScreen', 'Phone OTP verified successfully, calling verify-details API');

            // Get registration data from appStore
            const currentState = useAppStore.getState();
            const registration = currentState.uaePassRegistration;

            if (!registration?.customerId || !registration?.registrationId || !user?.mobile) {
                logger.error('PhoneOTPScreen', 'Missing required data for verify-details API', {
                    hasCustomerId: !!registration?.customerId,
                    hasRegistrationId: !!registration?.registrationId,
                    hasMobile: !!user?.mobile,
                });
                // Still navigate even if API call fails
                router.replace('/email-otp');
                return;
            }

            // Parse phone number to extract country code and mobile number
            const phoneStr = user.mobile.replace(/\s+/g, '');
            let countryCode = '971'; // Default to UAE
            let mobile = phoneStr;

            // Extract country code if phone starts with +
            if (phoneStr.startsWith('+')) {
                const codeMatch = phoneStr.match(/^\+\d{1,3}/)?.[0];
                if (codeMatch) {
                    countryCode = codeMatch.slice(1); // Remove + sign
                    mobile = phoneStr.replace(/^\+\d{1,3}/, '');
                }
            } else {
                // Try to match known country codes
                const knownCodes = ['971', '1', '44', '33', '49', '39', '34', '86', '81', '82', '91', '61', '55', '52', '7', '27'];
                const matchedCode = knownCodes.find(cc => phoneStr.startsWith(cc));
                if (matchedCode) {
                    countryCode = matchedCode;
                    mobile = phoneStr.slice(matchedCode.length);
                }
            }

            try {
                // Call verify-details API
                await customerDetailsService.verifyDetails({
                    customer_id: registration.customerId,
                    country_code: countryCode,
                    mobile: mobile,
                    email: user.email || undefined,
                    registration_id: registration.registrationId,
                });
                logger.info('PhoneOTPScreen', 'Verify-details API called successfully, navigating to email OTP');
            } catch (error) {
                logger.error('PhoneOTPScreen', 'Failed to call verify-details API', error);
                // Continue navigation even if API call fails
            }

            // Navigate to email OTP screen after phone verification
            router.replace('/email-otp');
        } else {
            logger.warn('PhoneOTPScreen', 'Phone OTP verification failed');
        }
    }, [verifyOtp, router, user]);

    const handleResendOTP = useCallback(async () => {
        logger.info('PhoneOTPScreen', 'Resending phone OTP');
        clearError();
        await resendOtp();
    }, [resendOtp, clearError]);

    // Clear error when OTP input changes
    const handleOTPChange = useCallback(() => {
        if (error) {
            clearError();
        }
    }, [error, clearError]);

    return (
        <OTPVerificationScreen
            type="phone"
            contactInfo={derivedPhone}
            progress={progress}
            otpLength={4}
            onOTPComplete={handleOTPComplete}
            onResendOTP={handleResendOTP}
            onOTPChange={handleOTPChange}
            verifyingOTP={isLoading}
            otpError={error}
            otpSuccess={isVerified}
        />
    );
});



export default PhoneOTPScreen;